import React, { useContext, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnglesLeft, faAnglesRight, faPlay } from "@fortawesome/free-solid-svg-icons";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation } from "swiper";
import { actionContext } from "../../apps/page";
import { exportNewSlotJson } from "../../data/gameData_slot";
import { Player } from "@lottiefiles/react-lottie-player";
import loading from "../../_asset/lottie/loading.json";
import api from "../../util/api";
import arrow from "../../_asset/image/pop_arrow.png";
import poppulartitlebg from "../../_asset/image/hades_title_bg.png";
import "../../_asset/css/popular_games.css";

interface HighlightGameInterface {
    uid: string,
    title: string,
    vendor: string,
    thumbnail: string
}

export function PopularGameList() {
    const [popularGames, setPopularGames] = useState<HighlightGameInterface[]>()
    const [viewportWidth, setViewportWidth] = useState<number>(window.innerWidth)
    const { User, SetLoginPop } = useContext(actionContext)

    useEffect(() => {
        api('/api/game/games/highlight/')
            .then((res) => res.json())
            .then((data: HighlightGameInterface[]) => {
                setPopularGames(data)
            })
            .catch(() => setPopularGames([]))
    }, [])

    useEffect(() => {
        const handleResize = () => {
            setViewportWidth(window.innerWidth)
        }
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const currentSlidesPerView = (() => {
        if (viewportWidth < 512) return 2
        if (viewportWidth < 768) return 3
        if (viewportWidth < 1060) return 4
        if (viewportWidth < 1460) return 5
        return 6
    })()

    const visiblePopularGames = useMemo(() => {
        if (!popularGames || popularGames.length === 0) return []
        if (popularGames.length === 1) return popularGames

        let expanded = [...popularGames]
        while (expanded.length <= currentSlidesPerView) {
            expanded = [...expanded, ...popularGames]
        }
        return expanded
    }, [popularGames, currentSlidesPerView])

    const gameCount = visiblePopularGames.length
    const canSlide = gameCount > currentSlidesPerView
    const shouldLoop = gameCount >= currentSlidesPerView * 2

    const openGame = (game: HighlightGameInterface) => {
        if (!User?.is_authenticated) {
            SetLoginPop()
            return
        }
        window.open(
            `/gameLobby/${game.vendor}/?uid=${game.uid}&vendor=${game.vendor}`,
            `${game.vendor}_${game.uid}`,
            `width=1200 height=640`
        )
    }

    const getVendorLogo = (game: HighlightGameInterface) => {
        const matchedGame = exportNewSlotJson.find((slotGame) => {
            return slotGame.id === game.uid || slotGame.vendor === game.vendor
        })
        return matchedGame?.logo || ''
    }

    return (
        <section className={'popular-games'}>
            <header>
                <div className="inner">
                    <button className="popular-arrow left">
                        <img src={arrow} alt="prev" />
                    </button>
                    <div className="title-wrapper">
                        <img src={poppulartitlebg} alt="타이틀 배경" className="title-bg" />
                        {/* <p className="title">인기게임</p> */}
                    </div>
                    <button className="popular-arrow right">
                        <img src={arrow} alt="next" />
                    </button>
                </div>
            </header>
            <main>
                <Swiper
                    key={`popular-${viewportWidth}-${gameCount}-${shouldLoop ? 'loop' : 'normal'}`}
                    className={'popular-game-swiper'}
                    slidesPerGroup={1}
                    spaceBetween={12}
                    loop={shouldLoop}
                    observer={true}
                    observeParents={true}
                    speed={700}
                    autoplay={canSlide ? {
                        delay: 1400,
                        disableOnInteraction: false,
                        reverseDirection: false,
                    } : false}
                    navigation={canSlide ? {
                        prevEl: '.popular-arrow.left',
                        nextEl: '.popular-arrow.right',
                    } : false}
                    allowTouchMove={canSlide}
                    watchOverflow={true}
                    breakpoints={{
                        0: { slidesPerView: 2 },
                        512: { slidesPerView: 3 },
                        768: { slidesPerView: 4 },
                        1060: { slidesPerView: 5 },
                        1460: { slidesPerView: 6 },
                    }}
                    modules={[Autoplay, Navigation]}
                >
                    {
                        visiblePopularGames.length > 0
                            ? visiblePopularGames.map((game, index) => {
                                const vendorLogo = getVendorLogo(game)
                                return (
                                    <SwiperSlide key={`${game.vendor}-${game.uid}-${index}`}>
                                        <button
                                            type={'button'}
                                            className={'popular-game-item'}
                                            onClick={() => openGame(game)}
                                        >
                                            <span className={'badge'}>인기</span>
                                            <img src={game.thumbnail} className={'game-image'} alt={game.title || game.vendor} />
                                            <div className={'overlay'}>
                                                <div className={'play'}>
                                                    <FontAwesomeIcon icon={faPlay} />
                                                </div>
                                            </div>
                                            <div className={'caption'}>
                                                {
                                                    vendorLogo
                                                        ? <img src={vendorLogo} alt={game.vendor} />
                                                        : <p className={'vendor-name'}>{game.vendor}</p>
                                                }
                                                <p>{game.title || game.vendor}</p>
                                            </div>
                                        </button>
                                    </SwiperSlide>
                                )
                            })
                            : [...Array(6)].map((_, index) => (
                                <SwiperSlide key={`popular-loading-${index}`}>
                                    <div className={'popular-game-item loading'}>
                                        <div className={'popular-loading'}>
                                            <Player
                                                src={loading}
                                                autoplay
                                                loop
                                                style={{ width: '8rem', height: '8rem' }}
                                            />
                                        </div>
                                    </div>
                                </SwiperSlide>
                            ))
                    }
                </Swiper>
            </main>
        </section>
    )
}
